import { useEffect, useRef, useState } from "react";
import { Microphone, Stop, X } from "@phosphor-icons/react";
import { kioskApi } from "@/lib/kioskApi";
import { useLiveSpeech } from "@/components/kiosk/useLiveSpeech";

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const words = (t) => (t || "").toLowerCase().match(/[a-z]+/g) || [];

// Menu names the live words seem to mention — shown as "possibly", never added to the order.
function provisional(menu, text) {
  const said = new Set(words(text));
  if (!said.size) return [];
  const count = {};
  menu.forEach((m) => new Set(words(m.name)).forEach((w) => { count[w] = (count[w] || 0) + 1; }));
  return menu.filter((m) => {
    const ws = words(m.name).filter((w) => w.length >= 3);
    const distinct = ws.filter((w) => count[w] === 1 && w.length >= 4);
    return distinct.some((w) => said.has(w) || said.has(`${w}s`)) || (ws.length > 0 && ws.every((w) => said.has(w)));
  }).slice(0, 6).map((m) => m.name);
}

// Start / Finish Speaking: the customer controls the recording (pauses never end it). Live words come from the
// browser's own recogniser where supported; the final words come from the speech service and are handed to onProcess.
export function SpeakPanel({ sessionId, menu, maxSeconds = 90, busy, onBeforeStart, onProcess, onActivity, onPhase, showGuesses = true, quick = false }) {
  const sp = useLiveSpeech({ maxSeconds });
  const [phase, setPhase] = useState("idle");
  const [note, setNote] = useState("");
  const sid = useRef(sessionId);

  useEffect(() => { sid.current = sessionId; sp.cancel(); setPhase("idle"); setNote(""); }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const begin = async () => {
    setNote("");
    onBeforeStart?.();
    try { await sp.start(); setPhase("recording"); } catch { setNote("The microphone isn't available — please allow it, or type your answer instead."); }
  };
  const done = async (hitLimit = false) => {
    const live = `${sp.finalText} ${sp.interim}`.trim();
    const rec = await sp.finish();
    const mine = sid.current;
    setPhase("checking");
    let final = live;
    // Short answers (yes/no, a table number, "go back") the device's free recogniser already heard clearly → no paid transcription.
    const freeOk = quick && sp.liveOk && sp.finalText.trim() && words(sp.finalText).length + (sp.finalText.match(/\d+/g) || []).length <= 5;
    if (rec && !freeOk) {
      try {
        const f = new FormData();
        f.append("audio", rec.blob, `speech.${rec.ext}`);
        const r = await kioskApi.post(`/sessions/${mine}/transcribe`, f, { timeout: 30000 });
        if (sid.current !== mine) return;
        if (r.data.text) final = r.data.text;
        else if (!live) final = "";
      } catch { /* keep the live words; the customer checks them on screen */ }
    }
    if (sid.current !== mine) return;
    const all = final.replace(/\s+/g, " ").trim();
    setPhase("idle");
    if (!all) { setNote("I didn't catch any speech — tap Start Speaking and try again."); return; }
    setNote(hitLimit ? "Recording limit reached — everything you said so far has been kept." : "");
    onProcess(all);
  };
  useEffect(() => { sp.onLimit(() => done(true)); }); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (phase !== "idle") onActivity?.(); }, [phase, sp.seconds]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onPhase?.(phase); }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const left = maxSeconds - sp.seconds;
  const liveText = `${sp.finalText}`.trim();
  const guesses = phase === "recording" && showGuesses ? provisional(menu, `${liveText} ${sp.interim}`) : [];
  return (
    <div className="space-y-3" data-testid="kiosk-speak-panel" data-phase={phase}>
      {phase === "idle" && (
        <button className="kiosk-big bg-[#e2572b] text-white disabled:opacity-50" disabled={busy} data-testid="kiosk-start-speaking" onClick={begin}>
          <Microphone size={26} weight="fill" /> Start Speaking
        </button>
      )}
      {phase === "recording" && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <button className="kiosk-big bg-white !text-[#0f2a3d]" data-testid="kiosk-finish-speaking" onClick={() => done(false)}><Stop size={24} weight="fill" /> Finish Speaking</button>
            <span className="inline-flex items-center gap-2 text-white text-lg" data-testid="kiosk-rec-timer"><span className="w-3 h-3 rounded-full bg-[#ef4444] animate-pulse" /> Listening… {mmss(sp.seconds)}</span>
            <button className="kiosk-pill bg-white/10 text-white" data-testid="kiosk-rec-cancel" onClick={() => { sp.cancel(); setPhase("idle"); }}><X size={16} /> Cancel recording</button>
          </div>
          {left <= 15 && <div className="text-[#fde68a]" data-testid="kiosk-rec-warning">{left} seconds left in this recording — your words so far will be kept.</div>}
          <div className="rounded-2xl bg-white/[0.07] border border-white/10 px-5 py-4 min-h-[88px] text-xl text-white" data-testid="kiosk-live-text">
            {liveText}{sp.interim && <span className="text-white/50 italic" data-testid="kiosk-live-interim"> {sp.interim}</span>}
            {!liveText && !sp.interim && <span className="text-white/40">{sp.liveOk ? "Start talking — take your time, pauses are fine." : "Live words aren't available on this device — your words will appear when you tap Finish Speaking."}</span>}
          </div>
          {guesses.length > 0 && <div className="text-white/60 text-sm" data-testid="kiosk-provisional">Possibly: {guesses.join(", ")} <span className="uppercase tracking-wide text-[10px] ml-1 px-1.5 py-0.5 rounded bg-white/10">not added yet</span></div>}
        </>
      )}
      {phase === "checking" && <div className="text-white/80 text-lg inline-flex items-center gap-2" data-testid="kiosk-speech-checking">Transcribing…<span className="kiosk-dots" aria-hidden="true"><i /><i /><i /></span></div>}
      {note && <div className="text-[#fde68a]" data-testid="kiosk-speak-note">{note}</div>}
    </div>
  );
}
